import { ReactElement, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../redux/hooks";
import { selectComplianceRiskByIMO, selectComplianceRiskLoading } from "../../features/screeningSlice";
import { Loading } from "../loading/Loading";
import { LliLinkButton, SeaSearcherLinkButton } from "@idwal/idwal-react-components";
import { sendGAEvent } from "../../services/AnalyticsService";
import { GA_BUTTON_EVENT, GA_CLICK_LLI_LINK } from "../../constants/GaEvents";
import "./ScreeningComplianceRisk.css";

export const ScreeningComplianceRisk = (): ReactElement => {
    const { imo } = useParams();
    const { t } = useTranslation("locale");
    const complianceRiskData = selectComplianceRiskByIMO(imo);
    const loading = useAppSelector(selectComplianceRiskLoading);

    const gaEvent = useCallback(() => {
        sendGAEvent(GA_BUTTON_EVENT, GA_CLICK_LLI_LINK);
    }, []);

    if (loading) {
        return <Loading />;
    }

    const isNoData = !complianceRiskData;
    const displayData = complianceRiskData || {
        vesselComplianceRiskScore: null,
        sanctionRisksCount: null,
        ownershipAndRegistryRisksCount: null,
        voyageRisksCount: null,
        vesselId: ''
    };

    const getRiskImage = (riskScore: string | null) => {
        if (isNoData || !riskScore) return '/risk-none.svg';
        
        switch (riskScore.toLowerCase()) {
            case 'high':
                return '/risk-high.svg';
            case 'medium':
                return '/risk-medium.svg';
            case 'low':
                return '/risk-low.svg';
            default:
                return '/risk-none.svg';
        }
    };

    const getRiskCircleClass = (count: number | null) => {
        const baseClass = 'compliance-risk-circle flex align-items-center justify-content-center border-circle font-bold flex-shrink-0';
        if (isNoData || count === null) return `${baseClass} compliance-risk-circle--empty`;
        return `${baseClass} compliance-risk-circle--transparent`;
    };

    const getRiskDisplayValue = (count: number | null) => {
        return (isNoData || count === null) ? '?' : count;
    };

    const riskCategories = [
        { label: t("complianceRisk.sanctionRisks"), count: displayData.sanctionRisksCount },
        { label: t("complianceRisk.ownershipRegistryRisks"), count: displayData.ownershipAndRegistryRisksCount },
        { label: t("complianceRisk.voyageRisks"), count: displayData.voyageRisksCount }
    ];

    const getRiskTextClass = () => {
        const baseClass = 'compliance-risk-text';
        return isNoData ? `${baseClass} compliance-risk-text--unavailable` : `${baseClass} compliance-risk-text--available`;
    };

    return (
        <div className="screening-compliance-risk">
              <div className="flex justify-content-between mb-4">
                  <p className="psc-header mb-0">
                       {t("complianceRisk.title")}
                  </p>
                  <LliLinkButton vesselId={displayData.vesselId} gaEvent={gaEvent} route="overview" />
              </div>
            
            <div className="grid mt-4">
                <div className="col-12 lg:col-7">
                    <div className="flex compliance-risk-card">
                        <div className="flex-1 flex flex-column align-items-center justify-content-center p-4">
                            <SeaSearcherLinkButton vesselId={`${displayData.vesselId}`} gaEvent={gaEvent} route="overview">
                                <img 
                                    src={getRiskImage(displayData.vesselComplianceRiskScore)}
                                    alt={isNoData ? t("complianceRisk.ratingUnavailable") : `${displayData.vesselComplianceRiskScore} ${t("complianceRisk.riskAlt")}`}
                                    style={{ width: '80px', height: '80px' }}
                                />
                            </SeaSearcherLinkButton>
                            <h3 className={`mt-2 ${getRiskTextClass()}`}>
                                {isNoData ? t("complianceRisk.ratingUnavailable") : `${displayData.vesselComplianceRiskScore} ${t("complianceRisk.riskAlt")}`}
                            </h3>
                        </div>
                        
                        <div className="border-left-2 border-300"></div>
                        
                        <div className="flex-1 p-4 flex flex-column justify-content-center">
                            {riskCategories.map((category, index) => (
                                <div key={index} className="flex align-items-center gap-3 mb-3">
                                    {category.count && category.count > 0 ? (
                                        <SeaSearcherLinkButton vesselId={`${displayData.vesselId}`} gaEvent={gaEvent} route="overview">
                                            <div className={getRiskCircleClass(category.count)}>
                                                {getRiskDisplayValue(category.count)}
                                            </div>
                                        </SeaSearcherLinkButton>
                                    ) : (
                                        <div className={getRiskCircleClass(category.count)}>
                                            {getRiskDisplayValue(category.count)}
                                        </div>
                                    )}
                                    <span className="text-base">{category.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-12 lg:col-5">
                    <div className="compliance-risk-description">
                        {t("complianceRisk.partnershipDescription")}{' '}
                        <SeaSearcherLinkButton key={0} vesselId={`${displayData.vesselId}`} gaEvent={gaEvent} route="overview">
                            <>
                            <a 
                                href={isNoData ? "#" : `https://www.seasearcher.com/vessel/${displayData.vesselId}/overview`}
                                target={isNoData ? "_self" : "_blank"}
                                rel={isNoData ? "" : "noopener noreferrer"}
                                className="text-blue-500"
                            >
                                {t("complianceRisk.lloydsList")}
                            </a>
                                .
                            </>
                        </SeaSearcherLinkButton>
     
                    </div>
                </div>
            </div>
        </div>
    );
};